import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Paired bars per competitor: this period vs. the same dates one month back.
// Seeing both bars side by side makes "getting more active" obvious without
// having to read the delta number.
export default function ActivityCompareChart({ competitors = [] }) {
  const { t } = useTranslation();

  const rows = useMemo(
    () =>
      competitors
        .map((competitor) => ({
          key: competitor.competitor_id,
          label: competitor.competitor_name,
          current: competitor.activity_trend?.current_proofs ?? 0,
          previous: competitor.activity_trend?.previous_proofs ?? 0,
          promotions: competitor.promotion_count ?? 0,
        }))
        .filter((row) => row.current > 0 || row.previous > 0),
    [competitors],
  );

  const max = useMemo(
    () => Math.max(1, ...rows.flatMap((row) => [row.current, row.previous])),
    [rows],
  );

  if (!rows.length) {
    return (
      <div className="dpi-chart-empty">
        {t("admin.insights.competitorIntelligence.noActivityData")}
      </div>
    );
  }

  return (
    <div className="dpi-ac-chart">
      <div className="dpi-ac-legend">
        <span className="dpi-ac-legend-item">
          <i className="dpi-ac-swatch dpi-ac-swatch--current" />
          {t("admin.insights.competitorIntelligence.thisPeriod")}
        </span>
        <span className="dpi-ac-legend-item">
          <i className="dpi-ac-swatch dpi-ac-swatch--previous" />
          {t("admin.insights.competitorIntelligence.lastMonth")}
        </span>
      </div>
      {rows.map((row) => (
        <div key={row.key} className="dpi-ac-row">
          <span className="dpi-ac-label">{row.label}</span>
          <div className="dpi-ac-bars">
            <div className="dpi-ac-bar-line">
              <div
                className="dpi-ac-bar dpi-ac-bar--current"
                style={{ width: `${(row.current / max) * 100}%` }}
              />
              <span className="dpi-ac-count">{row.current}</span>
            </div>
            <div className="dpi-ac-bar-line">
              <div
                className="dpi-ac-bar dpi-ac-bar--previous"
                style={{ width: `${(row.previous / max) * 100}%` }}
              />
              <span className="dpi-ac-count">{row.previous}</span>
            </div>
          </div>
          {row.promotions > 0 ? (
            <span className="dpi-ac-promo">
              {t("admin.insights.competitorIntelligence.promoCount", {
                count: row.promotions,
              })}
            </span>
          ) : (
            <span className="dpi-ac-promo dpi-muted">—</span>
          )}
        </div>
      ))}
    </div>
  );
}
